import { Component } from '@theme/component';

/**
 * Parse a complete ISO timestamp with an explicit UTC offset, rejecting invalid calendar dates.
 * @param {string} value
 * @returns {number} Timestamp in milliseconds, or NaN for an invalid setting.
 */
export function parsePromotionEnd(value) {
  const match = value.trim().match(
    /^(\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d)(Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/
  );
  if (!match) return NaN;

  const localTime = Date.parse(`${match[1]}Z`);
  if (!Number.isFinite(localTime) || new Date(localTime).toISOString().slice(0, 19) !== match[1]) return NaN;

  return Date.parse(match[0]);
}

/**
 * Switches copy without replacing the newsletter form, its values, or feedback.
 * Uses the visitor's clock to avoid Liquid page-cache delays. Without JavaScript,
 * automatic mode keeps the promotion copy; manual modes are rendered by Liquid.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [phaseContent]
 * @extends {Component<Refs>}
 */
export class NewsletterPromotion extends Component {
  /** @type {number | undefined} */
  #timer;
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    clearTimeout(this.#timer);
  }

  #initialize() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.addEventListener('visibilitychange', this.#update, { signal });
    window.addEventListener('pageshow', this.#update, { signal });
    this.#update();
  }

  #update = () => {
    clearTimeout(this.#timer);
    const mode = this.dataset.mode;
    const endsAt = parsePromotionEnd(this.dataset.promotionEndsAt ?? '');
    const now = Date.now();
    const automatic = mode === 'automatic' && Number.isFinite(endsAt);
    const phase = mode === 'after' || (automatic && now >= endsAt) ? 'after' : 'promotion';

    for (const content of this.refs.phaseContent ?? []) {
      content.hidden = content.dataset.phase !== phase;
    }
    this.dataset.phase = phase;

    // Recheck long schedules and pages left open across the boundary without overflowing timers.
    if (automatic && now < endsAt) {
      this.#timer = window.setTimeout(this.#update, Math.min(endsAt - now, 60_000));
    }
  };
}

if (!customElements.get('newsletter-promotion-component')) {
  customElements.define('newsletter-promotion-component', NewsletterPromotion);
}
